import { useContext, useEffect, useRef, useState } from 'react'

import {
  ButtonIcon,
  Callout,
  CalloutDescription,
  CalloutHeading,
  CodeBlock,
  Heading,
  Pill,
  Tag,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@optechai/design-system'
import lorikeetAvatar from '@optechai/design-system/assets/lorikeet_logomark_color.svg'
import { Link } from '@remix-run/react'
import { format } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'
import { AlertTriangleIcon, BookIcon, Info, ChevronDown } from 'lucide-react'
import { observer } from 'mobx-react-lite'

import type {
  CallEndReason,
  CloseTicketReason,
  EscalationReason,
  MessageCheckResult,
  ProcessingCancellationReason,
  SmallTalkCategory,
  ThinkingTicketEvent,
} from '@server/models/ticketEvent/ticketEvent.dto'
import type { TicketMessageAttachment } from '@server/models/ticketMessage/ticketMessageAttachment.dto'
import type {
  TicketActionResultForQA,
  TicketActionsResultsForQA,
  TicketEventForQA,
  MessageType,
} from '@server/services/ticket/ticket'

import type { CustomerForQA } from '../../../../server/src/models/customer/customer.dto'
import { getUserDisplayName } from '../../helpers/format'
import { useFeatureFlag } from '../../services/feature-flag'
import { PoweredBy } from '../powered-by/powered-by'
import {
  StaggerAnimation,
  type StaggerItemProps,
} from '../stagger-animation/stagger-animation'
import {
  type TicketOutcome,
  TicketOutcomePill,
} from '../ticket-outcome-pill/ticket-outcome-pill'
import { Tool } from '../tool/tool'
import type { WorkflowNodeTypeAndNew } from '../workflow-node-card'

import { bucketMessagesByTimestamp } from './batch-messages'
import { CustomisationContext, InvertAlignmentContext } from './context'
import { type MessageAuthor, MessageItem } from './message-item'

export type { MessageType }

type Person = {
  firstName?: string
  lastName?: string
  email?: string
  avatarUrl?: string
}

export type BasicNodeDetails = {
  id: string
  displayName: string
  type: WorkflowNodeTypeAndNew
}

export type MessageProps = {
  /**
   * Unique identifier for the message. A uuid.
   */
  id: string
  /**
   * Message content in most scenarios this will be markdown or HTML depending on where it originated.
   */
  content: string
  messageType: MessageType
  /**
   * Name of the subscriber who sent the message. Translated from the subscriber model.
   */
  subscriberName?: string
  customer?: Person
  user?: Person
  isFromBot?: boolean
  /** stringified date obj */
  createdAt?: string
  /** stringified date obj */
  updatedAt?: string
  remoteCreatedAt?: string
  originNodes?: BasicNodeDetails[]
  /**
   * Any attachments that are associated with the message.
   */
  attachments?: TicketMessageAttachment[]
}

export type TicketError = {
  errorMessage: string
  errorType: 'System' | 'Configuration'
  workflow?: {
    id: string
    displayName: string
    workflowContainerId: string
  }
  workflowNode?: {
    id: string
    displayName: string
  }
}

type MessageListProps = {
  /**
   * Is loading.
   */
  isLoading?: boolean
  /**
   * Whether to automatically scroll to the bottom when new messages are added.
   */
  autoScroll?: boolean
  /**
   * Collection of messages in the conversation.
   */
  messages: MessageProps[]
  /**
   * Additional classNames for the component.
   */
  className?: string
  /**
   * Events related to the ticket.
   */
  events: TicketEventForQA[]
  actions: TicketActionsResultsForQA

  customer?: CustomerForQA
  ticketTitle?: string
  ticketRemoteId?: string
  alwaysShowThinkingEvents?: boolean
  /** Optional offset from the bottom in REM for positioning the scroll arrow, accounts for external components like a send bar. */
  bottomOffsetRem?: number

  errors?: TicketError[]
  finalOutcome?: {
    outcome: TicketOutcome
    isAutomated: boolean
  }
}

const LORIKEET_AUTHOR = {
  src: lorikeetAvatar,
  initials: 'LK',
  align: 'right',
  name: 'Lori Keet',
} satisfies MessageAuthor

const LorikeetMessage = ({
  message,
  isInternal,
  isDraft,
  isLoading,
  isPreviousBot,
}: {
  isLoading?: boolean
  message?: MessageProps
  isInternal?: boolean
  isDraft?: boolean
  isPreviousBot?: boolean
}) => {
  const customisation = useContext(CustomisationContext)
  const author = {
    ...LORIKEET_AUTHOR,
  }

  if (customisation?.avatarUrl || customisation?.logoUrl) {
    author.src = (customisation.avatarUrl ?? customisation.logoUrl) as string
  }

  if (customisation?.botName) {
    author.name = customisation.botName
  }

  return (
    <MessageItem
      author={author}
      content={message?.content}
      createdAt={message?.createdAt}
      isBot={true}
      isDraft={isDraft}
      isInternal={isInternal}
      isLoading={isLoading}
      isPreviousBot={isPreviousBot}
      originNodes={message?.originNodes}
      remoteCreatedAt={message?.remoteCreatedAt}
    />
  )
}

const UserMessage = ({
  message,
  isInternal,
}: {
  message: MessageProps
  isInternal?: boolean
}) => {
  let authorInitials = '?'
  let authorName = isInternal ? 'Internal message' : 'Agent'
  if (message.user) {
    authorInitials =
      message.user.firstName && message.user.lastName
        ? `${message.user.firstName[0]}${message.user.lastName[0]}`
        : (message.subscriberName?.slice(0, 3).toUpperCase() ?? 'A')

    authorName = getUserDisplayName(message.user, 'Agent')
  }
  return (
    <MessageItem
      appearance="secondary"
      author={{
        initials: authorInitials,
        align: 'right',
        name: authorName,
      }}
      content={message.content}
      createdAt={message.createdAt}
      isInternal={isInternal}
      originNodes={message.originNodes}
      remoteCreatedAt={message.remoteCreatedAt}
    />
  )
}

const CustomerMessage = ({ message }: { message: MessageProps }) => {
  const { customer } = message
  const customerAvatarInitials = customer
    ? customer.firstName && customer.lastName
      ? `${customer.firstName[0]}${customer.lastName[0]}`
      : customer.email
        ? customer.email[0]
        : 'C'
    : 'C'
  // const name = getUserDisplayName(customer, 'Customer')
  return (
    <MessageItem
      appearance="primary"
      attachments={message.attachments}
      author={{
        initials: customerAvatarInitials!,
        src: customer?.avatarUrl,
        align: 'left',
        name: '',
      }}
      content={message.content}
      createdAt={message.createdAt}
      remoteCreatedAt={message.remoteCreatedAt}
    />
  )
}

const UnknownMessage = ({ message }: { message: MessageProps }) => {
  return (
    <MessageItem
      appearance="secondary"
      author={{
        initials: '?',
        align: 'right',
        name: 'Unknown',
      }}
      content={message.content}
      createdAt={message.createdAt}
      isUnknown={true}
      originNodes={message.originNodes}
      remoteCreatedAt={message.remoteCreatedAt}
    />
  )
}

type HumanReadableEvent = {
  message: string
  explanation?: string
  llmExplanation?: React.ReactNode
}

export const HumanReadableEscalationEvent = ({
  reason,
}: {
  reason: EscalationReason | undefined
}): HumanReadableEvent => {
  let explanation
  let message = 'Ticket escalated'

  switch (reason) {
    case 'WORKFLOW_ERROR':
      explanation = 'We hit a workflow error :('
      break
    case 'NO_WORKFLOW_MATCHES':
      message = 'Ticket handed off'
      explanation = "We couldn't find a workflow that matched any of the topics"
      break
    case 'WORKFLOW_SPLITTER':
      message = 'Ticket handed off'
      explanation =
        'Matched to a workflow being rolled out. Tagging and triage workflows will still be run'
      break
    case 'WORKFLOW_POWER_UP':
      message = 'Ticket handed off'
      explanation = 'We escalated per a configured workflow node'
      break
    case 'UNABLE_TO_GATHER_INFO_FROM_CUSTOMER':
      explanation =
        'We were unable to gather all required information from the customer'
      break
    case 'CUSTOMER_REQUESTED_HUMAN':
      explanation = 'The customer asked to talk to a human'
      break
    case 'AI_OFFERED_ESCALATION':
      explanation = 'The AI offered to escalate the ticket to a human'
      break
    case 'CUSTOMER_UPLOADED_ATTACHMENT':
      explanation = 'The customer uploaded an attachment'
      break
    case 'REPEATED_STATIC_RESPONSE':
      explanation = 'We were going to repeat the same response to the customer'
      break
    case 'RESPONSE_NODE_REPEATED_TOO_MANY_TIMES':
      explanation = 'We were going to run the same response node again'
      break
    case 'MATCHED_SAME_WORKFLOW':
      explanation = 'We were going to repeat a previously run workflow'
      break
    case 'COULDNT_ANSWER_CUSTOMER_WITH_AVAILABLE_INFO':
      message = 'Ticket handed off'
      explanation =
        "Lorikeet AI didn't think the response it had was enough to fully solve the customer's question"
      break
    case 'INVALID_RESPONSE':
      message = 'Ticket handed off'
      explanation = 'LLM generated an invalid response'
      break
    case 'OPTECH_DID_NOT_RESPOND':
      explanation = "We haven't responded to this ticket"
      break
    case 'NO_REFERENCE_MATERIAL':
      explanation = 'We could not find any reference material'
      break
    case 'POOR_REFERENCE_MATERIAL':
      explanation = 'We could not find any relevant reference material'
      break
    case 'REFERENCE_MATERIAL_RETRIEVAL_ERROR':
      explanation = 'We had an error retrieving reference material'
      break
    case 'CUSTOMER_AUTH_TOKEN_FETCH_ERROR':
      explanation = 'We had an error fetching the customer auth token'
      break
    case 'MULTIPLE_STATICALLY_PRODUCED_REPLY_TO_CUSTOMER_ACTIONS':
      explanation =
        'We needed to answer multiple customer questions, but one had a statically produced reply'
      break
    case 'NOT_ALL_TOPIC_WORKFLOWS_PRODUCED_REPLY_TO_CUSTOMER_ACTIONS':
      explanation =
        'We needed to answer multiple customer questions, but not all workflows produced a reply'
      break
    default:
      break
  }
  return { message, explanation }
}

export const HumanReadableNewTicketEvent = ({
  explanation,
}: {
  explanation: string | undefined
}): HumanReadableEvent => {
  return {
    message: 'New ticket',
    explanation: `Ticket was split into a new ticket: ${explanation}`,
  }
}

export const HumanReadableThinkingEvent = ({
  thought,
}: {
  thought?: ThinkingTicketEvent['data']
}): HumanReadableEvent => {
  if (!thought) {
    return { message: 'Thinking' }
  }

  switch (thought.type) {
    case 'REFERENCE_MATERIAL_SELECTED':
      return {
        message: 'Reference material selected',
        explanation: `Selected reference material: ${thought.referenceMaterials
          .map((ref) => ref.title)
          .join(', ')}`,
      }
    default:
      return { message: 'Thinking' }
  }
}

export const HumanReadableCallEndedEvent = ({
  callEndReason,
}: {
  callEndReason: CallEndReason | undefined
}): HumanReadableEvent => {
  if (!callEndReason) {
    return { message: 'Call ended' }
  }

  switch (callEndReason) {
    case 'CUSTOMER_ENDED':
      return { message: 'Call ended', explanation: 'Customer ended the call' }
    case 'AGENT_ENDED_WHEN_RESOLVED':
      return {
        message: 'Call ended',
        explanation: 'Agent ended the call because all topics were resolved',
      }
    case 'AGENT_ENDED_ON_ERROR':
      return {
        message: 'Call ended',
        explanation: 'Agent ended the call because it encountered an error',
      }
    case 'AGENT_TRANSFERRED_WHEN_UNRESOLVED':
      return {
        message: 'Call ended',
        explanation:
          'Agent transferred the call because there was an unresolved topic',
      }
    case 'AGENT_TRANSFERRED_ON_ERROR':
      return {
        message: 'Call ended',
        explanation: 'Call transferred because it encountered an error',
      }
    default:
      return { message: 'Call ended' }
  }
}

export const HumanReadableMessageCheckFailedEvent = ({
  failedChecks,
}: {
  failedChecks: MessageCheckResult[] | undefined
}): HumanReadableEvent => {
  if (!failedChecks) {
    // Shouldn't really ever happen, but just in case
    return {
      message: 'Message checks failed',
      explanation: 'Checks run on customer messages failed',
    }
  }

  const message =
    failedChecks.length > 1
      ? `${failedChecks.length} message checks failed`
      : 'Message check failed'

  const explanation = failedChecks
    .map(
      (check) =>
        `${check.name} failed with ${check.met === 'yes' ? 'high' : 'moderate'} confidence: ${check.explanation}`,
    )
    .join('\n')

  return {
    message,
    explanation,
  }
}

export const HumanReadableClosedTicketEvent = ({
  reason,
}: {
  reason: CloseTicketReason | undefined
}): HumanReadableEvent => {
  let explanation

  switch (reason) {
    case 'ESCALATION_CONFIG':
      explanation = 'Closed ticket due to out of hours escalation settings.'
      break
    case 'USER_ACTION':
      explanation =
        'This could be a user closing the ticket, or an automation that closed the ticket.'
      break
    case 'NO_OPEN_TOPICS':
      explanation = 'Closed because the customer raised no new topics'
      break
    case 'WAITING_FOR_CUSTOMER_RESPONSE_TIMEOUT':
      explanation = 'Closed because the customer did not respond before timeout'
      break
    case undefined:
      break
    default:
      const _exhaustiveCheck: never = reason
      break
  }

  return {
    message: 'Closing ticket',
    explanation,
  }
}

export const HumanReadableProcessingCancellationEvent = ({
  reason,
}: {
  reason: ProcessingCancellationReason | undefined
}): HumanReadableEvent => {
  let explanation
  const message = 'Ticket processing cancelled'

  switch (reason) {
    case 'EXCLUDED_TAGS':
      explanation =
        'Excluded tags were added to the ticket since we started processing'
      break
    case 'EXCLUDED_TAGS_RESPONSE':
      explanation =
        'Tags to exclude from responding were added to the ticket since we started processing'
      break
    case 'CANCELLED_TRIAGE_IN_FAVOUR_OF_RESPONSE':
      explanation =
        'We stopped triaging the ticket in favor of responding to the customer'
      break
    case 'ASSIGNED_TO_SOMEONE_ELSE':
      explanation = 'The ticket was assigned to someone else'
      break
    case 'TERMINAL_STATUS':
      explanation = 'Ticket reached a terminal status'
      break
    case 'PREVENT_REPEATED_EMAIL_RESPONSES':
      explanation =
        'We were going to respond to the customer, but we already did'
      break
    case 'UNKNOWN_MESSAGE_TYPE':
      explanation = 'We received a message of unknown type'
      break
    case 'NO_WORKFLOWS_ACTIVE':
      explanation = 'There are no workflows active for this ticket'
      break
    case 'NO_CUSTOMER_MESSAGES':
      explanation = 'Ticket has no customer messages'
      break
    case 'NO_MESSAGES':
      explanation = 'Ticket has no messages'
      break
    case 'NO_MESSAGE_CONTENT':
      explanation = 'Ticket has no message content'
      break
    case 'NO_INTERNAL_NOTES':
      explanation = 'Waiting for an internal note'
      break
    case 'NOT_ENDING_IN_CUSTOMER_MESSAGE':
      explanation = 'Ticket does not end in a customer message'
      break
    case 'AGENT_MESSAGE_ALREADY_PRESENT':
      explanation = 'Ticket already has a message from an agent'
      break
    case 'DATA_SOURCE_CANNOT_ACTION_TICKETS':
      explanation = 'Ticketing system integration is currently read-only'
      break
    case 'MISSING_REQUIRED_TAGS':
      explanation = 'Ticket is missing required tags'
      break
    case 'ENDS_WITH_BOT_RESPONSE':
      explanation = 'Ticket ends with a bot response'
      break
    case 'EXCLUDED_BY_EMAIL_IGNORE_LIST':
      explanation = 'Ticket is excluded by the email ignore list'
      break
    case 'UNEXPECTED_MESSAGE_COUNT':
      explanation = 'Ticket has an unexpected number of messages'
      break
    case 'NO_BRAND':
      explanation = "Ticket doesn't match to any segments"
      break
    case undefined:
      break
    default:
      const _exhaustiveCheck: never = reason
      break
  }
  return { message, explanation }
}

export const HumanReadableEscalationRequestEvent = ({
  explanation,
}: {
  explanation: string | undefined
}): HumanReadableEvent => {
  return {
    message: 'Ticket escalated due to request for human',
    llmExplanation: explanation ? (
      <Callout variant="default">
        <CalloutHeading>Explanation from model</CalloutHeading>
        <CalloutDescription>
          <CodeBlock>{explanation}</CodeBlock>
        </CalloutDescription>
      </Callout>
    ) : undefined,
  }
}

export const HumanReadableHostileMessageEvent = ({
  explanation,
}: {
  explanation: string | undefined
}): HumanReadableEvent => {
  return {
    message: 'Replied to hostile message',
    llmExplanation: explanation ? (
      <Callout variant="default">
        <CalloutHeading>Explanation from model</CalloutHeading>
        <CalloutDescription>
          <CodeBlock>{explanation}</CodeBlock>
        </CalloutDescription>
      </Callout>
    ) : undefined,
  }
}

export const HumanReadableSmallTalkEvent = ({
  category,
  explanation,
}: {
  category: SmallTalkCategory | undefined
  explanation: string | undefined
}): HumanReadableEvent => {
  let message = undefined
  switch (category) {
    case 'GREETING':
      message = 'Replied to greeting'
      break
    case 'INCOMPLETE_QUESTION':
      message = 'Replied to incomplete question'
      break
    default:
      message = 'Replied to small talk'
  }
  return {
    message,
    llmExplanation: explanation ? (
      <Callout variant="default">
        <CalloutHeading>Explanation from model</CalloutHeading>
        <CalloutDescription>{explanation}</CalloutDescription>
      </Callout>
    ) : undefined,
  }
}

export const HumanReadableClarificationEvent = ({
  explanation,
}: {
  explanation: string | undefined
}): HumanReadableEvent => {
  return {
    message: 'Clarifying vague topic',
    llmExplanation: explanation ? (
      <Callout variant="default">
        <CalloutHeading>Explanation from model</CalloutHeading>
        <CalloutDescription>{explanation}</CalloutDescription>
      </Callout>
    ) : undefined,
  }
}
export const HumanReadableDisambiguationEvent = ({
  ambiguousTopic,
  explanation,
}: {
  ambiguousTopic: string | undefined
  explanation: string | undefined
}): HumanReadableEvent => {
  return {
    message: ambiguousTopic
      ? `Disambiguating topic: ${ambiguousTopic}`
      : 'Disambiguating topic',
    llmExplanation: explanation ? (
      <div className="gap-y-s flex flex-col">
        <Text variant="p-s-bold">Explanation from model:</Text>
        <CodeBlock>{explanation}</CodeBlock>
      </div>
    ) : undefined,
  }
}

const ThinkingTicketEventMessage = ({
  data,
  isActiveThought,
}: {
  data: ThinkingTicketEvent['data']
  isActiveThought: boolean
}) => {
  const isInverted = useContext(InvertAlignmentContext)
  const alignmentClass = isInverted ? 'mr-auto ml-12' : 'ml-auto mr-12'
  const labelAlignmentClass = isInverted ? 'text-left' : 'text-right'
  const pillAlignmentClass = isInverted ? 'justify-start' : 'justify-end'
  const [expanded, setExpanded] = useState(false)
  const shown = expanded
    ? data.referenceMaterials
    : data.referenceMaterials.slice(0, 2)
  const extraCount = data.referenceMaterials.length - shown.length

  return (
    <div className={cn('my-xs max-w-md', alignmentClass)}>
      <Text
        className={cn('mb-1', labelAlignmentClass, {
          'animate-pulse': isActiveThought,
        })}
        color="subtle"
        variant="p-xs"
      >
        {isActiveThought ? 'Reading' : 'Read'}
      </Text>
      <div
        className={cn(
          'gap-xs mt-2 flex flex-wrap items-center',
          pillAlignmentClass,
        )}
      >
        {shown.map((ref) => (
          <Pill icon={BookIcon} key={ref.id} variant="white">
            {ref.url ? (
              <Link
                className="text-subtle"
                rel="noopener noreferrer"
                target="_blank"
                to={ref.url}
              >
                {ref.title}
              </Link>
            ) : (
              ref.title
            )}
          </Pill>
        ))}
        {!expanded && extraCount > 0 && (
          <Pill variant="grey">
            <button
              className="m-0 cursor-pointer border-none bg-transparent p-0 text-subtle"
              onClick={() => setExpanded(true)}
              type="button"
            >
              +{extraCount} more
            </button>
          </Pill>
        )}
        {expanded && data.referenceMaterials.length > 2 && (
          <Pill variant="grey">
            <button
              className="m-0 cursor-pointer border-none bg-transparent p-0 text-subtle"
              onClick={() => setExpanded(false)}
              type="button"
            >
              Show less
            </button>
          </Pill>
        )}
      </div>
    </div>
  )
}

const TicketEventMessage = ({ event }: { event: TicketEventForQA }) => {
  let message, explanation, llmExplanation
  const customisation = useContext(CustomisationContext)
  switch (event.type) {
    case 'ASSIGNED':
      {
        const userIdentifier = getUserDisplayName(
          event.assigneeUser,
          event.assigneeRemoteId || '',
        )
        message =
          event.assigneeRemoteId === null || event.assigneeRemoteId === ''
            ? 'Ticket unassigned'
            : `Ticket reassigned to ${userIdentifier}`
      }
      break
    case 'ESCALATED':
      {
        // Context for picking these messages:
        // https://optechai.slack.com/archives/C073DP3UHLM/p1718692263035419
        const {
          message: humanReadableMessage,
          explanation: humanReadableExplanation,
        } = HumanReadableEscalationEvent({
          reason: event.escalationReason,
        })

        message = humanReadableMessage
        explanation = humanReadableExplanation
      }
      break
    case 'PROCESSING_CANCELLED':
      {
        const {
          message: humanReadableMessage,
          explanation: humanReadableExplanation,
        } = HumanReadableProcessingCancellationEvent({
          reason: event.cancellationReason,
        })

        message = humanReadableMessage
        explanation = humanReadableExplanation
      }
      break
    case 'ESCALATION_REQUEST':
      {
        const {
          message: humanReadableMessage,
          llmExplanation: humanReadableLlmExplanation,
        } = HumanReadableEscalationRequestEvent({
          explanation: event.explanation,
        })
        message = humanReadableMessage
        llmExplanation = humanReadableLlmExplanation
      }
      break
    case 'HOSTILE_MESSAGE':
      {
        const {
          message: humanReadableMessage,
          llmExplanation: humanReadableLlmExplanation,
        } = HumanReadableHostileMessageEvent({
          explanation: event.explanation,
        })
        message = humanReadableMessage
        llmExplanation = humanReadableLlmExplanation
      }
      break
    case 'SMALL_TALK':
      {
        const {
          message: humanReadableMessage,
          llmExplanation: humanReadableLlmExplanation,
        } = HumanReadableSmallTalkEvent({
          category: event.category,
          explanation: event.explanation,
        })
        message = humanReadableMessage
        llmExplanation = humanReadableLlmExplanation
      }
      break
    case 'CLARIFICATION':
      {
        const {
          message: humanReadableMessage,
          llmExplanation: humanReadableExplanation,
        } = HumanReadableClarificationEvent({
          explanation: event.explanation,
        })
        message = humanReadableMessage
        llmExplanation = humanReadableExplanation
      }
      break
    case 'DISAMBIGUATION':
      {
        const {
          message: humanReadableMessage,
          llmExplanation: humanReadableLlmExplanation,
        } = HumanReadableDisambiguationEvent({
          ambiguousTopic: event.ambiguousTopic,
          explanation: event.explanation,
        })
        message = humanReadableMessage
        llmExplanation = humanReadableLlmExplanation
      }
      break
    case 'CLOSED':
      {
        const {
          message: humanReadableMessage,
          explanation: humanReadableExplanation,
        } = HumanReadableClosedTicketEvent({
          reason: event.closeReason,
        })

        message = humanReadableMessage
        llmExplanation = humanReadableExplanation
      }
      break
    case 'MESSAGE_CHECK_FAILED':
      {
        const {
          message: humanReadableMessage,
          explanation: humanReadableExplanation,
        } = HumanReadableMessageCheckFailedEvent({
          failedChecks: event.failedChecks,
        })

        message = humanReadableMessage
        llmExplanation = humanReadableExplanation
      }
      break
    case 'NEW_TICKET':
      {
        const {
          message: humanReadableMessage,
          explanation: humanReadableExplanation,
        } = HumanReadableNewTicketEvent({
          explanation: event.explanation,
        })

        message = humanReadableMessage
        llmExplanation = humanReadableExplanation
      }
      break
    case 'THINKING':
      {
        const {
          message: humanReadableMessage,
          explanation: humanReadableExplanation,
        } = HumanReadableThinkingEvent({
          thought: event.data,
        })

        message = humanReadableMessage
        llmExplanation = humanReadableExplanation
      }
      break
    case 'CALL_ENDED':
      {
        const {
          message: humanReadableMessage,
          explanation: humanReadableExplanation,
        } = HumanReadableCallEndedEvent({
          callEndReason: event.callEndReason,
        })

        message = humanReadableMessage
        llmExplanation = humanReadableExplanation
      }
      break
    default:
      message = 'Unknown event'
  }

  return (
    <Text className="w-full text-center text-subtle" variant="p-xs">
      {message}
      {(explanation || llmExplanation) && (
        <Tooltip>
          <TooltipTrigger className="align-top">
            <Info className="ml-xs" size={12} />
          </TooltipTrigger>
          {llmExplanation && (
            // TODO: Handle llm explanations and other explanations generically.
            <TooltipContent className="text-left">
              {llmExplanation}
            </TooltipContent>
          )}
          {explanation && (
            // Make this more generic, but for now, we're just showing the escalation
            // reason metadata.
            <TooltipContent className="text-left">
              <div>{explanation}</div>
              {event.escalationReasonMetadata ? (
                <CodeBlock>
                  {JSON.stringify(event.escalationReasonMetadata, null, 2)}
                </CodeBlock>
              ) : null}
            </TooltipContent>
          )}
        </Tooltip>
      )}
      {` • ${format(event.remoteCreatedAt ?? event.createdAt, customisation?.showVerboseTimestamps ? 'pp, d MMM yyyy' : 'pp')}`}
    </Text>
  )
}

const ActionMessage = observer(
  ({ actionResult }: { actionResult: TicketActionResultForQA }) => {
    const customisation = useContext(CustomisationContext)

    let message, details
    // Goals:
    // * Show the action if it errored
    // * Otherwise:
    //   * Show the action if the UI doesn't indicate the action -
    //     e.g. messages we've sent, notes we've posted, etc will show, so don't show those.
    //   * or if the action isn't useful to show, e.g. deleting paused workflow state.
    let shouldShowAction: boolean = !!actionResult.error
    switch (actionResult.action.actionType) {
      case 'ApplyTags':
        message = 'Applying tags'
        shouldShowAction = true
        details = (
          <div className="flex flex-wrap gap-2">
            {actionResult.action.tags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </div>
        )
        break
      case 'CloseTicket':
        message = 'Closing ticket'
        shouldShowAction = true
        break
      case 'CreateTicket':
        message = 'Creating new ticket'
        shouldShowAction = true
        break
      case 'DeletePausedWorkflowRun':
        message = 'Tidying prior paused workflow state'
        break
      case 'ReassignTicket':
        message = 'Reassigning ticket'
        break
      case 'PostInternalNote':
        message = 'Posting internal note'
        break
      case 'ReplyToCustomer':
        message = 'Replying to customer'
        break
      case 'PostToSlack':
        message = 'Posting to Slack'
        shouldShowAction = true
        details = (
          <CodeBlock>
            {{
              slackChannelId: actionResult.action.channelId,
              message: actionResult.action.text,
            }}
          </CodeBlock>
        )
        break
      case 'AddMessagesToTicket':
        message = 'Added messages to test ticket'
        break
      case 'PauseWorkflowRun':
        message = 'Pausing workflow'
        details =
          actionResult.action.pauseInfo?.reason ===
            'WAITING_FOR_ASYNC_TOOL_RESPONSE' ||
          actionResult.action.pauseInfo?.reason ===
            'WAITING_FOR_SYNC_TOOL_RESPONSE'
            ? 'Waiting for tool response'
            : actionResult.action.pauseInfo?.reason ===
                'WAITING_FOR_CUSTOMER_RESPONSE'
              ? 'Waiting for customer response'
              : undefined
        shouldShowAction = true
        break
      case 'EscalateToHuman':
        message = 'Attempting to escalate'
        break
      // TODO: Show prompt info etc as applicable
      case 'ReplyToCustomerFinal':
        message = 'Generating response to customer'
        break
      case 'SetPriority':
        message = 'Setting ticket priority'
        shouldShowAction = true
        details = <Pill>{actionResult.action.priority}</Pill>
        break
      case 'SetIntercomCustomAttributes':
        message = 'Setting custom attributes'
        shouldShowAction = true
        details = <CodeBlock>{actionResult.action.customAttributes}</CodeBlock>
        break
      case 'GatherFeedback':
        message = 'Feedback requested'
        shouldShowAction = true
        break
      default:
        message = 'Unknown action'
        break
    }

    if (!shouldShowAction) {
      return null
    }

    return (
      <Text
        className="w-full text-center"
        color={actionResult.error ? 'error' : 'subtle'}
        variant="p-xs"
      >
        {message}
        {(actionResult.error || details) && (
          <Tooltip>
            <TooltipTrigger style={{ verticalAlign: 'top' }}>
              {actionResult.error ? (
                <AlertTriangleIcon className="ml-xs" size={12} />
              ) : (
                <Info className="ml-xs" size={12} />
              )}
            </TooltipTrigger>
            <TooltipContent
              className={cn('max-h-80 max-w-md overflow-y-auto text-left')}
            >
              {details && <div>{details}</div>}
              {actionResult.error && (
                <>
                  <Callout variant="error">
                    <CalloutHeading>Error</CalloutHeading>
                    <CalloutDescription>
                      {actionResult.action.actionType ===
                      'DeletePausedWorkflowRun'
                        ? 'Unknown error'
                        : actionResult.error.message}
                    </CalloutDescription>
                  </Callout>
                  {actionResult.error.recommendedAction && (
                    <Callout variant="default">
                      <CalloutHeading>Recommended action</CalloutHeading>
                      <CalloutDescription>
                        {actionResult.error.recommendedAction}
                      </CalloutDescription>
                    </Callout>
                  )}
                  {actionResult.error.details && (
                    <div>
                      <Text variant="p-s-bold">Details</Text>
                      <div className="mt-xs">
                        <CodeBlock>
                          {JSON.stringify(actionResult.error.details, null, 2)}
                        </CodeBlock>
                      </div>
                    </div>
                  )}
                </>
              )}
            </TooltipContent>
          </Tooltip>
        )}
        {` • ${format(actionResult.endTime, customisation?.showVerboseTimestamps ? 'pp, d MMM yyyy' : 'pp')}`}
      </Text>
    )
  },
)

/**
 * The overall container for the message list, message name and related metadata.
 */
const AppMessageWidget = ({
  customer,
  ticketRemoteId,
  messages,
  isLoading,
  autoScroll,
  events = [],
  actions,
  ticketTitle,
  alwaysShowThinkingEvents,
  bottomOffsetRem = 0, // Default to 0 if not provided
  errors,
  finalOutcome,
}: MessageListProps) => {
  const [showScrollArrow, setShowScrollArrow] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Scroll to the bottom when new messages are added, if autoScroll is enabled.
  useEffect(() => {
    if (scrollRef.current && autoScroll) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages.length, isLoading, autoScroll]) // autoScroll was missing from dep array

  // Effect for showing/hiding the scroll-down arrow
  useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    const SCROLL_THRESHOLD = 20 // Pixels from bottom to hide arrow

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement
      let shouldShow = false
      if (scrollHeight > clientHeight) {
        shouldShow = scrollTop + clientHeight < scrollHeight - SCROLL_THRESHOLD
      }
      setShowScrollArrow(shouldShow)
    }

    // Initial check
    handleScroll()

    scrollElement.addEventListener('scroll', handleScroll)
    // Re-check when messages change or loading state changes
    return () => {
      scrollElement.removeEventListener('scroll', handleScroll)
    }
  }, [messages, isLoading]) // Re-evaluate when messages or loading state change
  return (
    <div
      className={cn(
        'relative grid h-full grid-rows-[auto_1fr] overflow-y-auto rounded-t-md bg-surface-card last:rounded-b-md',
      )}
    >
      {/* Content of AppMessageWidget starts here */}
      <div className="gap-xs p-m flex flex-col border-b border-solid bg-surface-card">
        <div className="flex w-full flex-row justify-between">
          <Tooltip>
            <TooltipTrigger>
              <Heading variant="h2">
                {customer?.displayName ?? `Ticket ${ticketRemoteId ?? ''}`}
              </Heading>
            </TooltipTrigger>
            <TooltipContent>
              {customer?.email && (
                <Text variant="p-s">
                  <span className="font-bold">Email:</span> {customer.email}
                </Text>
              )}
              {customer?.phoneNumber && (
                <Text variant="p-s">
                  <span className="font-bold">Phone:</span>{' '}
                  {customer.phoneNumber}
                </Text>
              )}
            </TooltipContent>
          </Tooltip>
          <TicketOutcomePill finalOutcome={finalOutcome} />
        </div>
        {ticketTitle && (
          <Text
            className="line-clamp-1 text-ellipsis text-subtle"
            variant="p-s"
          >
            {ticketTitle}
          </Text>
        )}
        {errors && errors.length > 0 && (
          <div className="mt-s gap-xs flex flex-col">
            {errors.slice(0, 2).map((error, index) => {
              return (
                <Callout key={index} variant="error">
                  <CalloutDescription>
                    <Text className="inline" variant="p-s">
                      Ticket processing failed
                    </Text>
                    {error.workflow && (
                      <Text className="inline" variant="p-s">
                        {' at workflow '}
                        <Link
                          className="text-sm underline hover:underline"
                          to={`/workflows/${error.workflow.workflowContainerId}/${error.workflow.id}`}
                        >
                          {error.workflow.displayName}
                        </Link>
                        {error.workflowNode && (
                          <>
                            {', node '}
                            <Link
                              className="text-sm underline hover:underline"
                              to={`/workflows/${error.workflow.workflowContainerId}/${error.workflow.id}?nodeId=${error.workflowNode.id}`}
                            >
                              {error.workflowNode.displayName}
                            </Link>
                          </>
                        )}
                        <Text className="inline" variant="p-s">
                          {'. '}
                          {error.errorType}
                          {' error: '} {error.errorMessage}
                        </Text>
                      </Text>
                    )}
                  </CalloutDescription>
                </Callout>
              )
            })}
          </div>
        )}
      </div>
      <div
        className="overflow-y-auto scroll-smooth bg-surface-sunken"
        ref={scrollRef}
      >
        <MessageThread
          actions={actions}
          alwaysShowThinkingEvents={alwaysShowThinkingEvents}
          events={events}
          hasEscalated={events.some(
            (event) => event.escalationReason !== undefined,
          )}
          isLoading={isLoading}
          messages={messages}
        />
      </div>
      {/* Scroll Arrow */}
      {showScrollArrow && (
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-0 left-1/2 z-10 -translate-x-1/2 transform"
          exit={{ opacity: 0, y: 10 }}
          initial={{ opacity: 0, y: 10 }}
          onClick={() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: 'smooth',
              })
            }
          }}
          style={{ bottom: `${bottomOffsetRem + 1}rem` }} // +1 rem for spacing from send bar
          transition={{ duration: 0.2 }}
        >
          <ButtonIcon
            className="text-gray-800 animate-bounce-twice rounded-full bg-surface-accent shadow-md hover:bg-surface-sunken"
            icon={ChevronDown}
            label="Scroll to bottom"
            type="button"
          />
        </motion.div>
      )}
    </div>
  )
}

export interface MessageThreadProps {
  actions: TicketActionsResultsForQA
  alwaysShowThinkingEvents?: boolean
  /**
   * Events related to the ticket.
   * @default []
   */
  events?: TicketEventForQA[]

  hasEscalated?: boolean
  /**
   * The side of the author avatar. Defaults to right.
   */
  invertMessageAlignment?: boolean
  isLoading?: boolean

  messages: MessageProps[]
}

const ToolMessage = ({ message }: { message: MessageProps }) => {
  const customisation = useContext(CustomisationContext)
  const messageData = JSON.parse(message.content)
  const toolName =
    message.messageType === 'INTERNAL_TOOL_INVOCATION_REQUEST'
      ? messageData.function.name
      : messageData.name

  return (
    <div className="-mx-l my-l !mt-xl py-l relative border-t">
      <Tooltip>
        <TooltipTrigger className="-top-l gap-y-xs absolute left-0 flex w-full flex-col items-center justify-center text-center">
          <Tool name={toolName || 'Internal Tool Invocation'} />
          {message.createdAt && (
            <Text color="subtle" variant="p-xs">
              {message.messageType === 'INTERNAL_TOOL_INVOCATION_REQUEST'
                ? 'Tool called '
                : 'Tool returned '}
              •{' '}
              {format(
                message.createdAt,
                customisation?.showVerboseTimestamps ? 'pp, d MMM yyyy' : 'pp',
              )}
            </Text>
          )}
        </TooltipTrigger>
        <TooltipContent className="max-h-80 max-w-md overflow-y-auto">
          <CodeBlock>
            {message.messageType === 'INTERNAL_TOOL_INVOCATION_REQUEST'
              ? messageData.function.arguments
              : messageData.response}
          </CodeBlock>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

export const MessageThread = ({
  messages,
  isLoading,
  events = [],
  actions,
  invertMessageAlignment,
  hasEscalated,
  alwaysShowThinkingEvents,
}: MessageThreadProps) => {
  // Message createdAt shouldn't be nullable...check why they are here.
  const listItems = [
    ...messages.map((message) => {
      return {
        type: 'message' as const,
        timestamp: message.remoteCreatedAt ?? message.createdAt!,
        message,
      }
    }),
    ...events.map((event) => {
      return {
        type: 'event' as const,
        timestamp: event.remoteCreatedAt ?? event.createdAt,
        event,
      }
    }),
    ...actions.actionsTaken.map((action) => {
      return {
        type: 'action' as const,
        timestamp: action.startTime,
        actionResult: action,
      }
    }),
    ...(actions.actionError
      ? [
          {
            type: 'action' as const,
            timestamp: actions.actionError.startTime,
            actionResult: actions.actionError,
          },
        ]
      : []),
  ]

  const displayMessages = bucketMessagesByTimestamp(listItems)
  const context = useContext(CustomisationContext)

  return (
    <InvertAlignmentContext.Provider value={invertMessageAlignment}>
      <ol className="space-y-l p-l">
        {context?.useSubtleBranding && <PoweredBy variant="subtle" />}
        {displayMessages.map(({ timestamp, messages }) => {
          return (
            <MessageGroup
              alwaysShowThinkingEvents={alwaysShowThinkingEvents}
              key={timestamp}
              messages={messages}
              timestamp={timestamp}
            />
          )
        })}
        <AnimatePresence>
          {isLoading && !hasEscalated && (
            <motion.div {...messageAnimationProps} key="loading-indicator">
              <LorikeetMessage isLoading />
            </motion.div>
          )}
        </AnimatePresence>
      </ol>
    </InvertAlignmentContext.Provider>
  )
}

type MessageGroupItem =
  | { type: 'message'; message: MessageProps }
  | { type: 'event'; event: TicketEventForQA }
  | { type: 'action'; actionResult: TicketActionResultForQA }

const MessageGroupMessageItem = ({
  message,
  previousMessage,
}: {
  message: MessageProps
  previousMessage?: MessageGroupItem
}) => {
  switch (message.messageType) {
    case 'CUSTOMER':
      return <CustomerMessage message={message} />
    case 'USER':
      // For backwards compatibility.
      return message.isFromBot ? (
        <LorikeetMessage
          isPreviousBot={
            previousMessage?.type === 'message' &&
            previousMessage.message.messageType === 'BOT_RESPONSE'
          }
          message={message}
        />
      ) : (
        <UserMessage message={message} />
      )
    case 'BOT_RESPONSE':
      return (
        <LorikeetMessage
          isPreviousBot={
            previousMessage?.type === 'message' &&
            previousMessage.message.messageType === 'BOT_RESPONSE'
          }
          message={message}
        />
      )
    case 'PENDING_RESPONSE':
      return <LorikeetMessage key={message.id} message={message} />
    case 'DRAFT_RESPONSE':
      return <LorikeetMessage isDraft key={message.id} message={message} />
    case 'INTERNAL_USER_NOTE':
      // For backwards compatibility.
      return message.isFromBot ? (
        <LorikeetMessage isInternal key={message.id} message={message} />
      ) : (
        <UserMessage isInternal key={message.id} message={message} />
      )
    case 'INTERNAL_MODEL_NOTE':
    case 'INTERNAL_MODEL_PRIVATE_NOTE':
      return <LorikeetMessage isInternal message={message} />
    case 'INTERNAL_TOOL_INVOCATION_DATA':
    case 'INTERNAL_TOOL_INVOCATION_REQUEST':
      return <ToolMessage message={message} />
    case 'UNKNOWN':
      return <UnknownMessage key={message.id} message={message} />
  }
}

const messageAnimationProps: Partial<StaggerItemProps> = {
  animate: { opacity: 1, y: 0 },
  as: 'li' as const,
  initial: { opacity: 0, y: 8 },
  transition: {
    type: 'spring',
    duration: 0.5,
  },
}

const MessageGroup = ({
  timestamp,
  messages,
  alwaysShowThinkingEvents = false,
}: {
  timestamp: string
  messages: MessageGroupItem[]
  alwaysShowThinkingEvents?: boolean
}) => {
  const showThinkingEvents = useFeatureFlag('show-thinking-events')
  const customisation = useContext(CustomisationContext)

  if (!messages.length) {
    return null
  }

  return (
    <div className="gap-y-s flex flex-col">
      <Text
        className="text-center"
        color="subtle"
        suppressHydrationWarning
        variant="p-xs"
      >
        {format(
          timestamp,
          customisation?.showVerboseTimestamps ? 'pp, d MMM yyyy' : 'pp',
        )}
      </Text>
      <ol className="space-y-m">
        <StaggerAnimation.Root initial={false} staggerDelay={0.15}>
          {messages.map((item, i) => {
            if (item.type === 'message') {
              const message = item.message
              const previousMessage = i > 0 ? messages[i - 1] : undefined
              // we can't use timestamp because it will get updated when the message is inserted into the db
              const dateStamp = message.createdAt
                ? new Date(message.createdAt).toISOString().split('T')[0]
                : undefined
              // Use stable animation id as customer message start out as id:stub
              const pseudoId = `message-${message.messageType}-${dateStamp}-${message.content.trim()}`
              const key =
                message.messageType === 'BOT_RESPONSE' ? message.id : pseudoId

              return (
                <StaggerAnimation.Item
                  {...messageAnimationProps}
                  key={key}
                  staggerId={key}
                >
                  <MessageGroupMessageItem
                    message={message}
                    previousMessage={previousMessage}
                  />
                </StaggerAnimation.Item>
              )
            }

            if (item.type === 'action') {
              const actionId = `action-${i}`

              return (
                <StaggerAnimation.Item
                  {...messageAnimationProps}
                  key={actionId}
                  staggerId={actionId}
                >
                  <ActionMessage actionResult={item.actionResult} />
                </StaggerAnimation.Item>
              )
            }

            if (item.event.type === 'THINKING') {
              if (!showThinkingEvents) {
                return null
              }

              // Thinking events are only relevant if there has not been a bot response after
              const moreRecentBotResponseExists = isThereAMoreRecentBotResponse(
                messages,
                new Date(item.event.createdAt),
              )
              if (moreRecentBotResponseExists && !alwaysShowThinkingEvents) {
                return null
              }

              return (
                <StaggerAnimation.Item
                  {...messageAnimationProps}
                  key={item.event.id}
                  staggerId={item.event.id}
                >
                  <ThinkingTicketEventMessage
                    data={item.event.data!}
                    isActiveThought={!moreRecentBotResponseExists}
                  />
                </StaggerAnimation.Item>
              )
            }

            return (
              <StaggerAnimation.Item
                {...messageAnimationProps}
                key={item.event.id}
                staggerId={item.event.id}
              >
                <TicketEventMessage event={item.event} />
              </StaggerAnimation.Item>
            )
          })}
        </StaggerAnimation.Root>
      </ol>
    </div>
  )
}
export { AppMessageWidget as MessageList }

function isThereAMoreRecentBotResponse(
  messages: MessageGroupItem[],
  timestamp: Date,
) {
  return messages.some(
    (message) =>
      message.type === 'message' &&
      (message.message.messageType === 'BOT_RESPONSE' ||
        message.message.messageType === 'PENDING_RESPONSE') &&
      message.message.createdAt &&
      new Date(message.message.createdAt) > timestamp,
  )
}
